import { useEffect, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { copyText } from "@/lib/copyText";

// Curated, well-established pairings (heading + body) commonly recommended
// for web typography. Loaded live from Google Fonts for preview.
const PAIRINGS = [
  // Times New Roman is a system font (not on Google Fonts), so it isn't
  // included in the live Google Fonts <link> loaded below -- browsers
  // ship it natively already, so it renders correctly regardless.
  { heading: "Times New Roman", body: "Arial", vibe: "Classic / traditional print-style", system: true },
  {
    heading: "Playfair Display",
    body: "Source Sans 3",
    vibe: "Editorial / elegant",
  },
  { heading: "Poppins", body: "Inter", vibe: "Modern / clean SaaS" },
  {
    heading: "Montserrat",
    body: "Merriweather",
    vibe: "Bold heading, readable body",
  },
  { heading: "Oswald", body: "Open Sans", vibe: "Strong / condensed headings" },
  { heading: "Lora", body: "Nunito Sans", vibe: "Warm / friendly editorial" },
  { heading: "Raleway", body: "Roboto", vibe: "Minimal / geometric" },
  { heading: "Abril Fatface", body: "Lato", vibe: "Bold display / fashion" },
  { heading: "Space Grotesk", body: "Work Sans", vibe: "Tech / startup" },
  { heading: "Cormorant Garamond", body: "Karla", vibe: "Classic / refined" },
  { heading: "Bebas Neue", body: "Rubik", vibe: "Impactful / poster-style" },
] as const;

// Google Fonts rejects the whole request if any family asks for a weight it doesn't have
// (Abril Fatface and Bebas Neue only come in 400), so each family states its own weights.
const SINGLE_WEIGHT = new Set(["Abril Fatface", "Bebas Neue"]);
const SERIF = new Set(["Times New Roman", "Playfair Display", "Merriweather", "Lora", "Abril Fatface", "Cormorant Garamond"]);
const gfFamily = (f: string, weights: string) => `family=${f.replace(/ /g, "+")}${SINGLE_WEIGHT.has(f) ? "" : `:wght@${weights}`}`;
const fallbackOf = (f: string) => (SERIF.has(f) ? "serif" : "sans-serif");

const TOOL_INFO_CONTENT = {
  what: `Shows curated heading-and-body font combinations that are known to work well together, since pairing two fonts that clash is one of the most common design mistakes.`,
  how: `Enter your own heading and body text to preview it in each pairing (or leave the defaults), browse the results, and copy the CSS and Google Fonts import for the pairing you like — so what you copy matches what you actually saw, not a generic placeholder.`,
  tip: `As a general rule, pairing two fonts from very different categories (like a serif heading with a sans-serif body) tends to work better than pairing two fonts that are similar but not quite matching.`,
};

export default function FontPairingTool() {
  const [headingText, setHeadingText] = useState("Your Heading Here");
  const [bodyText, setBodyText] = useState(
    "The quick brown fox jumps over the lazy dog. Replace this with your own body copy to see how it reads in each pairing."
  );
  const [previewingHeading, setPreviewingHeading] = useState<string | null>(null);

  useEffect(() => {
    const families = Array.from(
      new Set(PAIRINGS.filter(p => !("system" in p && p.system)).flatMap(p => [p.heading, p.body]))
    )
      .map(f => gfFamily(f, "400;600;700"))
      .join("&");
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const buildCss = (heading: string, body: string, isSystem?: boolean) => {
    const importLine = isSystem
      ? `/* Times New Roman ships with every OS — no font import needed */`
      : `/* Google Fonts import */\n@import url('https://fonts.googleapis.com/css2?${gfFamily(heading, "600;700")}&${gfFamily(body, "400;500")}&display=swap');`;
    return `/* Heading */\nfont-family: '${heading}', ${fallbackOf(heading)};\n\n/* Body */\nfont-family: '${body}', ${fallbackOf(body)};\n\n${importLine}`;
  };

  const { logUsageNow } = useLogUsageOnAction();

  const copyPairing = async (heading: string, body: string, isSystem?: boolean) => {
    const allowed = await logUsageNow("copy");
    if (!allowed) return;
    void copyText(buildCss(heading, body, isSystem), "Copied CSS + font import");
  };

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <div><Label className="text-xs">Heading Text</Label><Input aria-label="Heading Text" value={headingText} onChange={e => setHeadingText(e.target.value)} /></div>
        <div><Label className="text-xs">Body Text</Label><Input aria-label="Body Text" value={bodyText} onChange={e => setBodyText(e.target.value)} /></div>
      </div>
      <div className="space-y-4">
        {PAIRINGS.map(pair => {
          const isSystem = "system" in pair && pair.system;
          const isPreviewing = previewingHeading === pair.heading;
          return (
          <Card key={pair.heading} className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="text-xs text-muted-foreground min-w-0 break-words">{pair.vibe} — {pair.heading} + {pair.body}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPreviewingHeading(isPreviewing ? null : pair.heading)}
                >
                  {isPreviewing ? "Hide CSS" : "Preview CSS"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyPairing(pair.heading, pair.body, isSystem)}
                >
                  Copy CSS
                </Button>
              </div>
            </div>
            <p
              style={{ fontFamily: `'${pair.heading}', ${fallbackOf(pair.heading)}` }}
              className="text-2xl font-bold mb-1"
            >
              {headingText || pair.heading}
            </p>
            <p
              style={{ fontFamily: `'${pair.body}', ${fallbackOf(pair.body)}` }}
              className="text-sm text-muted-foreground"
            >
              {bodyText || `${pair.body} — The quick brown fox jumps over the lazy dog.`}
            </p>
            {isPreviewing && (
              <pre className="mt-3 p-3 rounded-lg border bg-muted/30 font-mono text-xs whitespace-pre-wrap break-all">
                {buildCss(pair.heading, pair.body, isSystem)}
              </pre>
            )}
          </Card>
          );
        })}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
